const KNOWN_INTERACTION_TYPES = new Set(['radio', 'confirm']);

const SUGGESTION_RE = /<suggestion>([\s\S]*?)<\/suggestion>/g;
const ENTITY_SELF_CLOSE_RE = /<entity\s+([\s\S]*?)\/>/g;
const MARKDOWN_RE = /<markdown>([\s\S]*?)<\/markdown>/;
const ATTR_RE = /(\w+)="([^"]*)"/g;

const parseSuggestions = (block) => {
  const items = [];
  let match;
  SUGGESTION_RE.lastIndex = 0;
  // eslint-disable-next-line no-cond-assign
  while ((match = SUGGESTION_RE.exec(block)) !== null) {
    items.push(match[1].trim());
  }
  return items;
};

const parseCard = (content) => {
  try {
    return JSON.parse(content.trim());
  } catch {
    return null;
  }
};

const parseAttrs = (attrsStr) => {
  const attrs = {};
  if (!attrsStr) return attrs;
  let match;
  ATTR_RE.lastIndex = 0;
  // eslint-disable-next-line no-cond-assign
  while ((match = ATTR_RE.exec(attrsStr)) !== null) {
    attrs[match[1]] = match[2];
  }
  return attrs;
};

const parseEntities = (block) => {
  const entities = [];
  let match;
  ENTITY_SELF_CLOSE_RE.lastIndex = 0;
  // eslint-disable-next-line no-cond-assign
  while ((match = ENTITY_SELF_CLOSE_RE.exec(block)) !== null) {
    const attrs = parseAttrs(match[1]);
    if (Object.keys(attrs).length > 0) entities.push(attrs);
  }
  return entities;
};

/**
 * Parse an <interaction ...>…</interaction> block.
 *
 * Returns `{ interaction, entities }`:
 *   - `interaction` carries the outer-tag attrs (uid, type, action, …) plus any
 *     inner `<markdown>` body and child `<entity/>` list. `null`-valued attrs
 *     are omitted so confirm/select/radio consumers can type-narrow on presence.
 *   - `entities` is the flat list of child entities (duplicated here so legacy
 *     entity-card rendering keeps working unchanged during streaming).
 */
const parseInteraction = (content, openAttrs) => {
  const rawType = openAttrs.type || null;
  const interactionType = rawType && KNOWN_INTERACTION_TYPES.has(rawType) ? rawType : null;
  const rawEntities = parseEntities(content);
  const entities = interactionType === 'radio'
    ? rawEntities.map((e) => ({ ...e, _interactionType: interactionType }))
    : rawEntities;
  const markdownMatch = content.match(MARKDOWN_RE);
  const resolvedAttrs = interactionType ? openAttrs : Object.fromEntries(
    Object.entries(openAttrs).filter(([k]) => k !== 'type'),
  );
  const interaction = {
    ...Object.fromEntries(Object.entries(resolvedAttrs).filter(([, v]) => v)),
    entities: [...entities],
  };
  if (markdownMatch) {
    const md = markdownMatch[1].trim();
    if (md) interaction.markdown = md;
  }
  return { interaction, entities };
};

const TAGS = [
  { name: 'suggestions', close: '</suggestions>', parse: parseSuggestions, key: 'suggestions', multi: true },
  { name: 'card', close: '</card>', parse: parseCard, key: 'cards', multi: false },
  { name: 'interaction', close: '</interaction>', kind: 'interaction' },
];

/**
 * Find the earliest opening tag in `text`. Handles tags with attributes
 * (e.g. <interaction type="confirm">) by matching the tag-name prefix and
 * scanning forward to the closing '>'.
 */
const findFirstTag = (text) => {
  let earliest = null;
  TAGS.forEach((tag) => {
    const prefix = `<${tag.name}`;
    const idx = text.indexOf(prefix);
    if (idx < 0) return;
    const charAfter = text[idx + prefix.length];
    if (charAfter !== '>' && charAfter !== ' ' && charAfter !== '\n' && charAfter !== '\t') return;
    const closeAngle = text.indexOf('>', idx + prefix.length);
    if (closeAngle < 0) return;
    if (earliest === null || idx < earliest.idx) {
      const openTag = text.slice(idx, closeAngle + 1);
      const selfClose = openTag.endsWith('/>');
      const rawAttrsStr = openTag.slice(prefix.length, selfClose ? -2 : -1);
      earliest = {
        idx,
        tag,
        openTagLen: closeAngle + 1 - idx,
        attrs: parseAttrs(rawAttrsStr),
        selfClose,
      };
    }
  });
  return earliest;
};

const findPartialTagAtEnd = (text) => {
  let minIdx = -1;
  TAGS.forEach((tag) => {
    const open = `<${tag.name}`;
    for (let len = open.length - 1; len >= 1; len -= 1) {
      if (text.endsWith(open.slice(0, len))) {
        const idx = text.length - len;
        if (minIdx === -1 || idx < minIdx) minIdx = idx;
        break;
      }
    }
  });
  return minIdx;
};

const dispatch = (result, found, content) => {
  if (found.tag.kind === 'interaction') {
    const { interaction, entities } = parseInteraction(content, found.attrs);
    result.interactions.push(interaction);
    result.entities.push(...entities);
    return;
  }
  const parsed = found.tag.parse(content);
  if (found.tag.multi) {
    result[found.tag.key].push(...(parsed || []));
  } else if (parsed) {
    result[found.tag.key].push(parsed);
  }
};

const processRawText = (raw) => {
  const result = { text: '', afterText: '', suggestions: [], cards: [], entities: [], interactions: [], parts: [] };
  let remaining = raw;
  let seenTag = false;
  let pendingText = '';

  const flushText = () => {
    if (!pendingText) return;
    result.parts.push({ type: 'text', content: pendingText });
    pendingText = '';
  };

  const addText = (chunk) => {
    if (seenTag) result.afterText += chunk;
    else result.text += chunk;
    pendingText += chunk;
  };

  const pushEntityPart = (entities) => {
    if (entities.length > 0) result.parts.push({ type: 'entities', entities });
  };

  while (remaining.length > 0) {
    const found = findFirstTag(remaining);

    if (!found) {
      const partialIdx = findPartialTagAtEnd(remaining);
      addText(partialIdx >= 0 ? remaining.slice(0, partialIdx) : remaining);
      break;
    }

    addText(remaining.slice(0, found.idx));
    remaining = remaining.slice(found.idx);

    if (found.selfClose) {
      flushText();
      const before = result.entities.length;
      dispatch(result, found, '');
      seenTag = true;
      pushEntityPart(result.entities.slice(before));
      remaining = remaining.slice(found.openTagLen);
      // eslint-disable-next-line no-continue
      continue;
    }

    const closeIdx = remaining.indexOf(found.tag.close);
    if (closeIdx === -1) {
      if (found.tag.kind === 'interaction') {
        const partialContent = remaining.slice(found.openTagLen);
        const { entities } = parseInteraction(partialContent, found.attrs);
        result.entities.push(...entities);
        if (entities.length > 0) { flushText(); pushEntityPart(entities); }
      }
      break;
    }

    const content = remaining.slice(found.openTagLen, closeIdx);
    flushText();
    const before = result.entities.length;
    dispatch(result, found, content);
    seenTag = true;

    if (found.tag.kind === 'interaction') pushEntityPart(result.entities.slice(before));

    remaining = remaining.slice(closeIdx + found.tag.close.length);
  }

  flushText();
  return result;
};

export default processRawText;
